import React, { ReactElement, useEffect, useRef } from 'react'
import { AnimatePresence } from 'framer-motion'
import { Box, Theme } from '@mui/material'
import { HistoryLocation } from '@gatsbyjs/reach-router'

import { pageTransition, StyledMotionMain } from '@utils/motion'
import useContentfulNavigation from '@components/site/hooks/contentful/useContentfulNavigation'
import Header from '@components/site/container/Header/'
import Footer from '@components/site/container/Footer/'
import useGlobalState from '@components/site/hooks/useGlobalState'
import PortalProvider from '@components/site/providers/PortalProvider'
import useContentfulFormSteps from '@components/site/hooks/contentful/useContentfulFormSteps'

export type LayoutProps = MCDC.Props.IDefault & {
  location: HistoryLocation
  pageContext?: MCDC.Contentful.IPageContext
}

export default function Layout({
  children,
  location,
  pageContext,
}: LayoutProps): ReactElement {
  const globalState = useGlobalState()
  const footerPortalRef = useRef<HTMLDivElement>(null)
  const { homePage, formPage, mainPages, metaPages } = useContentfulNavigation()
  const { formStepPages } = useContentfulFormSteps()

  return (
    <>
      <Header
        navItems={[homePage, ...mainPages]}
        navMetaItems={[formPage]}
        formStepItems={formStepPages}
        location={location}
      />
      <PortalProvider portalRef={footerPortalRef} location={location}>
        <AnimatePresence
          exitBeforeEnter
          onExitComplete={() => globalState.setLocation(location)}
        >
          <StyledMotionMain
            key={location.pathname}
            {...pageTransition}
            sx={{
              position: 'relative',
              minHeight: '80vh',
              overflow: 'hidden',
              pt: 16,
              'header.is-formPage + &': {
                pt: 24,
              },
            }}
          >
            <Box
              sx={(muiTheme: Theme) => ({
                ...muiTheme.mixins.conatainerModuleSpacing('large'),
              })}
            >
              {children}
            </Box>
          </StyledMotionMain>
        </AnimatePresence>
      </PortalProvider>
      <Box
        sx={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 100,
          pointerEvents: 'none',
        }}
      >
        <div ref={footerPortalRef} />
      </Box>
      <Footer navItems={metaPages} />
    </>
  )
}
